import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ProcessingRule } from "@/entities/all";
import { toast } from "sonner";

export default function DeleteProcessingRuleDialog({ isOpen, onClose, onRuleDeleted, rule }) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!rule) return;
    setIsDeleting(true);
    try {
      await ProcessingRule.delete(rule.id);
      toast.success('Processing rule deleted successfully');
      onRuleDeleted();
    } catch (error) {
      console.error('Error deleting processing rule:', error);
      if (error.message?.includes('foreign key') || error.message?.includes('RESTRICT')) {
        toast.error('Cannot delete rule: it is currently used by element versions');
      } else {
        toast.error('Failed to delete processing rule');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const usageCount = parseInt(rule?.usage_count, 10) || 0;
  const canDelete = usageCount === 0;

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Processing Rule</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            {canDelete ? (
              <>
                <p>
                  Are you sure you want to delete the processing rule "{rule?.rule_name}"?
                </p>
                <p className="text-slate-500">
                  This action cannot be undone. The rule and all its test cases will be permanently removed.
                </p>
              </>
            ) : (
              <>
                <p className="text-amber-600 font-medium">
                  This rule cannot be deleted because it is currently used by {usageCount} element version{usageCount !== 1 ? 's' : ''}.
                </p>
                <p className="text-slate-500">
                  To delete this rule, first remove it from all element versions that reference it.
                </p>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          {canDelete && (
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete Rule'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
